/*
License

Copyright (c) 2022 Chukwubuikem Ume-Ugwa

Permission is hereby witheld, any person obtaining a copy
of this software and associated documentation files (the "Software"), cannot
use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software. These restrictions can be only lifted with
a written permission from Chukwubuikem Ume-Ugwa.
*/
import { WalletNotConnectedError } from '@solana/wallet-adapter-base';
import { Transaction } from '@solana/web3.js';
import {
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  AccountLayout,
  getAssociatedTokenAddress,
} from '@solana/spl-token';
import {
  PublicKey,
  TransactionInstruction,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
} from '@solana/web3.js';
import BufferLayout from 'buffer-layout';
import BN from 'bn.js';

import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';

export const MINT_ADDRESS = isDev()
  ? new PublicKey('6UXXuQpyCrQrjWQxseeqBCKAquz7hQpxWvjU1bVzep6e')
  : new PublicKey('HCpyiCXvziKByTGoQMLUmooAWBkCLvZmzB9D6TyPdACR');

// Root-Token-Account: Aeri9aw2sK6dE1gxvBaEXTzCv6sF4RTnxvTusm7tCNFh
// DEV ATA: FQDpPYJVWzXWJjbXYHHLXYmGrYkMCMcfaeGkLiyfGHWJ 20000000000
/*
=================Abandonded======================
token-address: HtjjWzUBD4padv6d4sk1CySogxCR6y2MRzKwpr7b2GsE
token-account: GcFBDLPfwFRpQ3A5VYxCwfoVx6ZBWWXq2Hwrouy4xj1C
*/
export const DECIMALS = 100_000_000;
export function isDev() {
  return !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
}

export function getNetwork() {
  if (isDev()) {
    return 'http://localhost:8099';
  }
  return "https://musicq-7f247.uc.r.appspot.com";
}

export async function SendTransaction(
  connection,
  payerAssociatedTokenAccountAddress,
  payerPublicKey,
  receiverPublicKey,
  amount,
  signTransaction
) {
  if (!receiverPublicKey || !amount) return;
  const mint = MINT_ADDRESS;
  try {
    if (!receiverPublicKey || !signTransaction)
      throw new WalletNotConnectedError();

    const payerTokenAccount = await getAccountInfo(
      connection,
      payerAssociatedTokenAccountAddress
    );

    const receiverAssociatedTokenAccountAddress =
      await getAssociatedTokenAddress(mint, receiverPublicKey);

    const receiverTokenAccount = await getAccountInfo(
      connection,
      receiverAssociatedTokenAccountAddress
    );

    const transaction = new Transaction().add(
      createTransferInstruction(
        payerTokenAccount.address, // source
        receiverTokenAccount.address, // dest
        payerPublicKey,
        amount * DECIMALS,
        [],
        TOKEN_PROGRAM_ID
      )
    );

    const blockHash = await connection.getRecentBlockhash();
    transaction.feePayer = await payerPublicKey;
    transaction.recentBlockhash = await blockHash.blockhash;

    const signed = await signTransaction(transaction);
    await connection.sendRawTransaction(signed.serialize());
    return true;
  } catch (error) {
    // NOOP
  }
  return false;
}

export async function getOrCreateAssociatedTokenAccount(
  connection,
  payer,
  mint,
  owner,
  signTransaction,
  commitment = 'confirmed',
  programId = TOKEN_PROGRAM_ID
) {
  // Generate PDA
  const associatedToken = await getAssociatedTokenAddress(mint, owner);

  let account;
  try {
    account = await getAccountInfo(
      connection,
      associatedToken,
      commitment,
      programId
    );
  } catch (error) {
    if (
      error.message === 'TokenAccountNotFoundError' ||
      error.message === 'TokenInvalidAccountOwnerError'
    ) {
      if (
        await createAssociatedTokenAccount(
          connection,
          associatedToken,
          payer,
          mint,
          owner,
          signTransaction
        )
      ) {
        account = await getAccountInfo(
          connection,
          associatedToken,
          commitment,
          programId
        );
      } else {
        throw Error('Account creation failed');
      }
    } else {
      throw error;
    }
  }

  if (!account.mint.equals(mint)) {
    throw Error('TokenInvalidMintError');
  }

  if (!account.owner.equals(owner)) {
    throw new Error('TokenInvalidOwnerError');
  }

  return account;
}

export async function hasTokenAccount(
  connection,
  associatedTokenAccountAddress,
  commitment = 'confirmed',
  programId = TOKEN_PROGRAM_ID
) {
  try {
    await getAccountInfo(
      connection,
      associatedTokenAccountAddress,
      commitment,
      programId
    );
  } catch (error) {
    if (error.message === 'TokenAccountNotFoundError') {
      return false;
    }
  }

  return true;
}

export async function createAssociatedTokenAccount(
  connection,
  associatedTokenAccountAddress,
  payer,
  mint,
  owner,
  signTransaction,
  programId = TOKEN_PROGRAM_ID,
  associatedTokenProgramId = ASSOCIATED_TOKEN_PROGRAM_ID
) {
  try {
    const transaction = new Transaction().add(
      createAssociatedTokenAccountInstruction(
        payer,
        associatedTokenAccountAddress,
        owner,
        mint,
        programId,
        associatedTokenProgramId
      )
    );

    const blockHash = await connection.getRecentBlockhash();
    transaction.feePayer = await payer;
    transaction.recentBlockhash = await blockHash.blockhash;

    const signed = await signTransaction(transaction);
    const signature = await connection.sendRawTransaction(signed.serialize());
    await connection.confirmTransaction(signature);

    return true;
  } catch (error) {
    // NOOP
  }

  return false;
}

export function createAssociatedTokenAccountInstruction(
  payer,
  associatedTokenAccountAddress,
  owner,
  mint,
  programId = TOKEN_PROGRAM_ID,
  associatedTokenProgramId = ASSOCIATED_TOKEN_PROGRAM_ID
) {
  const keys = [
    { pubkey: payer, isSigner: true, isWritable: true },
    {
      pubkey: associatedTokenAccountAddress,
      isSigner: false,
      isWritable: true,
    },
    { pubkey: owner, isSigner: false, isWritable: false },
    { pubkey: mint, isSigner: false, isWritable: false },
    { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    { pubkey: programId, isSigner: false, isWritable: false },
    { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
  ];

  return new TransactionInstruction({
    keys: keys,
    programId: associatedTokenProgramId,
    data: Buffer.alloc(0),
  });
}

export const TokenInstruction = {
  InitializeMint: 0,
  InitializeAccount: 1,
  InitializeMultisig: 2,
  Transfer: 3,
  Approve: 4,
  Revoke: 5,
  SetAuthority: 6,
  MintTo: 7,
  Burn: 8,
  CloseAccount: 9,
  FreezeAccount: 10,
  ThawAccount: 11,
  TransferChecked: 12,
  ApproveChecked: 13,
  MintToChecked: 14,
  BurnChecked: 15,
  InitializeAccount2: 16,
  SyncNative: 17,
  InitializeAccount3: 18,
  InitializeMultisig2: 19,
  InitializeMint2: 20,
};

/**
 * Construct a Transfer instruction
 *
 * @param source       Source account
 * @param destination  Destination account
 * @param owner        Owner of the source account
 * @param amount       Number of tokens to transfer
 * @param multiSigners Signing accounts if `owner` is a multisig
 * @param programId    SPL Token program account
 *
 * @return Instruction to add to a transaction
 */
export function createTransferInstruction(
  source,
  destination,
  owner,
  amount,
  multiSigners = [],
  programId = TOKEN_PROGRAM_ID
) {
  const dataLayout = BufferLayout.struct([
    BufferLayout.u8('instruction'),
    BufferLayout.blob(8, 'amount'),
  ]);

  const keys = addSigners(
    [
      { pubkey: source, isSigner: false, isWritable: true },
      { pubkey: destination, isSigner: false, isWritable: true },
    ],
    owner,
    multiSigners
  );

  const data = Buffer.alloc(dataLayout.span);
  dataLayout.encode(
    {
      instruction: TokenInstruction.Transfer,
      amount: new TokenAmount(amount).toBuffer(),
    },
    data
  );

  return new TransactionInstruction({ keys, programId, data });
}

export function addSigners(keys, ownerOrAuthority, multiSigners) {
  if (multiSigners.length) {
    keys.push({ pubkey: ownerOrAuthority, isSigner: false, isWritable: false });
    for (const signer of multiSigners) {
      keys.push({
        pubkey: signer.receiverPublicKey,
        isSigner: true,
        isWritable: false,
      });
    }
  } else {
    keys.push({ pubkey: ownerOrAuthority, isSigner: true, isWritable: false });
  }
  return keys;
}

class TokenAmount extends BN {
  /**
   * Convert to Buffer representation
   */
  toBuffer() {
    const a = super.toArray().reverse();
    const b = Buffer.from(a);
    if (b.length === 8) {
      return b;
    }

    if (b.length >= 8) {
      throw new Error('TokenAmount too large');
    }

    const zeroPad = Buffer.alloc(8);
    b.copy(zeroPad);
    return zeroPad;
  }

  /**
   * Construct a TokenAmount from Buffer representation
   */
  static fromBuffer(buffer) {
    if (buffer.length !== 8) {
      throw new Error(`Invalid buffer length: ${buffer.length}`);
    }

    return new BN(
      [...buffer]
        .reverse()
        .map(i => `00${i.toString(16)}`.slice(-2))
        .join(''),
      16
    );
  }
}

export const AccountState = {
  Uninitialized: 0,
  Initialized: 1,
  Frozen: 2,
};

export async function getAccountInfo(
  connection,
  address,
  commitment = 'confirmed',
  programId = TOKEN_PROGRAM_ID
) {
  const info = await connection.getAccountInfo(address, commitment);
  if (!info) throw new Error('TokenAccountNotFoundError');
  if (!info.owner.equals(programId))
    throw new Error('TokenInvalidAccountOwnerError');
  if (info.data.length !== AccountLayout.span)
    throw new Error('TokenInvalidAccountSizeError');

  const rawAccount = AccountLayout.decode(Buffer.from(info.data));

  return {
    address,
    mint: rawAccount.mint,
    owner: rawAccount.owner,
    amount: rawAccount.amount,
    delegate: rawAccount.delegateOption ? rawAccount.delegate : null,
    delegatedAmount: rawAccount.delegatedAmount,
    isInitialized: rawAccount.state !== AccountState.Uninitialized,
    isFrozen: rawAccount.state === AccountState.Frozen,
    isNative: !!rawAccount.isNativeOption,
    rentExemptReserve: rawAccount.isNativeOption ? rawAccount.isNative : null,
    closeAuthority: rawAccount.closeAuthorityOption
      ? rawAccount.closeAuthority
      : null,
  };
}
